import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const ZoneCard = ({ title, description, image, icon: Icon, features, link, delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <>
      <style>{`
        @keyframes data-stream {
          0% { transform: translateY(-100%); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translateY(100%); opacity: 0; }
        }
        
        @keyframes scan-line {
          0% { top: 0%; }
          100% { top: 100%; }
        }
        
        @keyframes glitch-anim {
          0% { clip-path: inset(40% 0 61% 0); }
          20% { clip-path: inset(92% 0 1% 0); }
          40% { clip-path: inset(43% 0 1% 0); }
          60% { clip-path: inset(25% 0 58% 0); }
          80% { clip-path: inset(54% 0 7% 0); }
          100% { clip-path: inset(58% 0 43% 0); }
        }
        
        @keyframes border-pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        
        .data-stream {
          position: absolute;
          width: 2px;
          height: 20px;
          background: linear-gradient(to bottom, transparent, #00ffff, transparent);
          animation: data-stream 2s linear infinite;
        }
        
        .scan-line {
          position: absolute;
          left: 0;
          width: 100%;
          height: 1px;
          background: linear-gradient(90deg, transparent, #00ffff, transparent);
          animation: scan-line 3s linear infinite;
        }
        
        .cyber-clip {
          clip-path: polygon(
            0 0, calc(100% - 12px) 0, 100% 12px,
            100% 100%, 12px 100%, 0 calc(100% - 12px)
          );
        }
        
        .icon-clip {
          clip-path: polygon(
            0 4px, 4px 0, calc(100% - 4px) 0, 100% 4px,
            100% calc(100% - 4px), calc(100% - 4px) 100%, 4px 100%, 0 calc(100% - 4px)
          );
        }
        
        .glitch-effect {
          position: relative;
        }
        
        .glitch-effect::before,
        .glitch-effect::after {
          content: attr(data-text);
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
        }
        
        .glitch-effect:hover::before {
          animation: glitch-anim 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) infinite;
          color: #ff00ff;
          z-index: -1;
          opacity: 0.8;
        }
        
        .glitch-effect:hover::after {
          animation: glitch-anim 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) reverse infinite;
          color: #00ffff;
          z-index: -2;
          opacity: 0.8;
        }
        
        .holographic-border {
          position: relative;
        }
        
        .holographic-border::before {
          content: '';
          position: absolute;
          inset: 0;
          padding: 2px;
          background: linear-gradient(45deg, #00ffff, #ff00ff, #ffff00, #00ffff);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          animation: border-pulse 2s ease-in-out infinite;
          clip-path: polygon(
            0 0, calc(100% - 12px) 0, 100% 12px,
            100% 100%, 12px 100%, 0 calc(100% - 12px)
          );
        }
        
        .neon-text {
          text-shadow: 
            0 0 3px currentColor,
            0 0 6px currentColor;
        }
        
        .neon-text-subtle {
          text-shadow: 
            0 0 2px currentColor,
            0 0 4px currentColor;
        }
        
        .corner-accent {
          position: absolute;
          width: 16px;
          height: 16px;
          border: 2px solid #00ffff;
        }
        
        .corner-tl { top: -1px; left: -1px; border-right: none; border-bottom: none; }
        .corner-tr { top: -1px; right: -1px; border-left: none; border-bottom: none; }
        .corner-bl { bottom: -1px; left: -1px; border-right: none; border-top: none; }
        .corner-br { bottom: -1px; right: -1px; border-left: none; border-top: none; }
      `}</style>

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="group relative bg-black/90 backdrop-blur-xl border-2 border-cyan-500/30 hover:border-cyan-500 transition-all duration-500 overflow-hidden cyber-clip holographic-border"
        data-testid={`zone-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
        style={{
          boxShadow: isHovered 
            ? '0 0 30px rgba(0, 255, 255, 0.3), inset 0 0 30px rgba(0, 255, 255, 0.1)' 
            : '0 0 15px rgba(0, 255, 255, 0.1), inset 0 0 15px rgba(0, 255, 255, 0.05)'
        }}
      >
        {/* Corner Accents */}
        <div className="corner-accent corner-tl" />
        <div className="corner-accent corner-tr" />
        <div className="corner-accent corner-bl" />
        <div className="corner-accent corner-br" />

        {/* Scan Line Effect */}
        <div className="scan-line" />

        {/* Data Stream Effects */}
        <div className="data-stream" style={{ left: '10%', animationDelay: '0s' }} />
        <div className="data-stream" style={{ left: '50%', animationDelay: '0.5s' }} />
        <div className="data-stream" style={{ left: '90%', animationDelay: '1s' }} />

        {/* Content Container - Compact Spacing */}
        <div className="relative p-5 space-y-4">
          {/* Icon and Title Row */}
          <div className="flex items-center space-x-3">
            {/* Icon Container */}
            <motion.div
              whileHover={{ scale: 1.1, rotate: 5 }}
              className="relative p-2.5 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border-2 border-cyan-500/40 group-hover:border-cyan-500 transition-all duration-300 icon-clip"
              style={{
                boxShadow: isHovered 
                  ? '0 0 20px rgba(0, 255, 255, 0.5), inset 0 0 10px rgba(0, 255, 255, 0.2)' 
                  : '0 0 10px rgba(0, 255, 255, 0.3)'
              }}
            >
              <Icon className="h-5 w-5 text-cyan-400 neon-text" />
              
              {/* Icon corner markers */}
              <div className="absolute top-0 left-0 w-1 h-1 bg-cyan-500" />
              <div className="absolute top-0 right-0 w-1 h-1 bg-purple-500" />
              <div className="absolute bottom-0 left-0 w-1 h-1 bg-yellow-500" />
              <div className="absolute bottom-0 right-0 w-1 h-1 bg-cyan-500" />
            </motion.div>

            {/* Title */}
            <h3 
              className="text-xl font-heading font-bold text-cyan-400 neon-text-subtle glitch-effect flex-1"
              data-text={title}
            >
              {title}
            </h3>

            {/* Status Indicator */}
            <div className="flex items-center space-x-1">
              <div 
                className="w-2 h-2 bg-green-500 rounded-full animate-pulse" 
                style={{ boxShadow: '0 0 8px #00ff00' }}
              />
              <span className="text-[10px] font-mono text-green-500/70 uppercase">Active</span>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-cyan-100/80 leading-relaxed font-mono">
            {description}
          </p>

          {/* Features List - Compact */}
          {features && features.length > 0 && (
            <div className="space-y-1.5 pt-2 border-t border-cyan-500/20">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center space-x-2 text-xs text-cyan-300/70 font-mono group/item hover:text-cyan-300 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <div className="w-1 h-1 bg-cyan-500" style={{ boxShadow: '0 0 3px #00ffff' }} />
                    <div className="w-2 h-[1px] bg-cyan-500/50" />
                  </div>
                  <span className="group-hover/item:translate-x-1 transition-transform">{feature}</span>
                </motion.div>
              ))}
            </div>
          )}

          {/* CTA Link */}
          {link && (
            <div className="pt-3 border-t border-cyan-500/20">
              <Link
                to={link}
                data-testid={`zone-card-link-${title.toLowerCase().replace(/\s+/g, '-')}`}
                className="inline-flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-all group/link relative"
              >
                <span className="text-xs font-bold uppercase tracking-wider font-mono neon-text">
                  Access Zone
                </span>
                <motion.div
                  whileHover={{ x: 5 }}
                  className="relative"
                >
                  <ArrowRight className="h-4 w-4" />
                  <div 
                    className="absolute inset-0 bg-cyan-400 blur-md opacity-0 group-hover/link:opacity-50 transition-opacity"
                  />
                </motion.div>

                {/* Animated underline */}
                <motion.div
                  className="absolute bottom-0 left-0 h-[1px] bg-gradient-to-r from-cyan-500 to-purple-500"
                  initial={{ width: 0 }}
                  whileHover={{ width: '100%' }}
                  transition={{ duration: 0.3 }}
                />
              </Link>
            </div>
          )}
        </div>

        {/* Holographic Overlay on Hover */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.5 }}
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(0, 255, 255, 0.1), transparent 50%)'
          }}
        />

        {/* Grid Overlay */}
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0, 255, 255, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 255, 255, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px'
          }}
        />

        {/* Version Tag */}
        <div className="absolute top-2 right-2 text-[9px] font-mono text-cyan-500/40 uppercase">
          v2.{String(delay * 10).padStart(2, '0')}
        </div>
      </motion.div>
    </>
  );
};

export default ZoneCard;